/**
 * Celebration Animations
 * Provides confetti and checkmark animations for game completions
 */

/**
 * Check if animations should be shown
 * Respects prefers-reduced-motion setting
 * @returns {boolean} Whether animations should play
 */
export function shouldAnimate() {
    if (window.matchMedia) {
        return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return true;
}

/**
 * Show confetti animation
 * Creates 50 colored confetti pieces that fall from random positions
 */
export function showConfetti() {
    if (!shouldAnimate()) {
        return;
    }

    const colors = 10; // Number of color variations
    const pieces = 50; // Number of confetti pieces

    for (let i = 0; i < pieces; i++) {
        const confetti = document.createElement('div');
        confetti.className = `confetti color-${i % colors}`;

        // Random horizontal position
        confetti.style.left = Math.random() * 100 + '%';

        // Random delay for staggered effect
        confetti.style.animationDelay = Math.random() * 0.5 + 's';

        // Random duration for varied falling speeds
        confetti.style.animationDuration = (Math.random() * 1 + 2) + 's';

        // Random size variation
        const size = Math.random() * 5 + 8;
        confetti.style.width = size + 'px';
        confetti.style.height = size + 'px';

        document.body.appendChild(confetti);

        // Remove after animation completes
        confetti.addEventListener('animationend', () => {
            confetti.remove();
        });
    }
}

/**
 * Show checkmark animation in a container
 * @param {HTMLElement} container - Container element to show checkmark in
 * @returns {HTMLElement} The checkmark container element
 */
export function showCheckmark(container) {
    if (!container) {
        console.warn('No container provided for checkmark animation');
        return null;
    }

    const checkmarkContainer = document.createElement('div');
    checkmarkContainer.className = 'checkmark-container';

    const checkmark = document.createElement('div');
    checkmark.className = 'checkmark';

    checkmarkContainer.appendChild(checkmark);
    container.style.position = 'relative';
    container.appendChild(checkmarkContainer);

    // Remove after animation completes
    setTimeout(() => {
        checkmarkContainer.remove();
    }, 1000);

    return checkmarkContainer;
}

/**
 * Show success pulse animation on an element
 * @param {HTMLElement} element - Element to pulse
 */
export function showSuccessPulse(element) {
    if (!shouldAnimate() || !element) {
        return;
    }

    element.classList.add('success-pulse');

    // Remove class after animation completes
    setTimeout(() => {
        element.classList.remove('success-pulse');
    }, 600);
}

/**
 * Show celebration text
 * @param {HTMLElement} container - Container element
 * @param {string} text - Celebration text to display
 */
export function showCelebrationText(container, text = '🎉 Complete!') {
    if (!container) {
        return;
    }

    const celebration = document.createElement('div');
    celebration.className = 'celebration';
    celebration.textContent = text;

    container.style.position = 'relative';
    container.appendChild(celebration);

    // Fade out and remove after animation
    setTimeout(() => {
        celebration.classList.add('fade-out');
        setTimeout(() => {
            celebration.remove();
        }, 500);
    }, 1500);
}

/**
 * Show sparkle effect at specific coordinates
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 */
export function showSparkle(x, y) {
    if (!shouldAnimate()) {
        return;
    }

    const sparkle = document.createElement('div');
    sparkle.className = 'sparkle';
    sparkle.style.left = x + 'px';
    sparkle.style.top = y + 'px';

    document.body.appendChild(sparkle);

    // Remove after animation completes
    setTimeout(() => {
        sparkle.remove();
    }, 1000);
}

